import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Outlet, useRouter, useRouterState } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { setUnauthorizedHandler } from '@/api/client'
import { Sidebar, SidebarNav } from './Sidebar'
import { Topbar, MOD } from './Topbar'
import { CommandPalette } from './CommandPalette'
import { NotificationsDrawer } from './NotificationsDrawer'
import { useGlobalShortcuts } from './useShortcuts'
import { Logo } from './Logo'
import { NAV, EXTRA_ROUTES } from './nav'
import { DialogContent } from '@/components/ui/dialog'
import { Kbd } from '@/components/ui/misc'
import { TaskFormDialog } from '@/features/tasks/TaskFormDialog'
import { useLiveUpdates } from '@/lib/live'
import { ui, useUI } from '@/lib/store'
import { useT } from '@/i18n'
import { usePageTracking } from './usePageTracking'
import { HelpPanel } from '@/help/HelpPanel'
import { HELP_DOCK_QUERY, HELP_PANEL_WIDTH, useMediaQuery } from '@/help/useMediaQuery'
import { TourRunner } from '@/tour/TourRunner'
import { useWelcomeTour } from '@/tour/useWelcomeTour'
import { PwaBootstrap } from './Pwa'

export function AppShell() {
  const router = useRouter()
  const qc = useQueryClient()
  const live = useLiveUpdates()
  useGlobalShortcuts()
  usePageTracking()
  useWelcomeTour()
  // The docked help panel pushes the content aside instead of covering it.
  const helpOpen = useUI((s) => s.helpOpen)
  const wide = useMediaQuery(HELP_DOCK_QUERY)
  const helpDocked = helpOpen && wide

  // Any 401 from an action (expired session) sends the user back to login.
  React.useEffect(() => {
    setUnauthorizedHandler(() => {
      const href = router.state.location.href
      qc.clear()
      void router.navigate({ to: '/login', search: { redirect: href } as never })
    })
    return () => setUnauthorizedHandler(null)
  }, [router, qc])

  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const mainRef = React.useRef<HTMLElement>(null)
  React.useEffect(() => {
    ui.set({ mobileNavOpen: false })
  }, [pathname])

  return (
    <div className="flex min-h-dvh">
      <a href="#main" className="sr-only z-50 rounded bg-surface px-3 py-2 focus:not-sr-only focus:fixed focus:left-2 focus:top-2">
        Skip to content
      </a>
      <Sidebar />
      <MobileNav />
      <div className="flex min-w-0 flex-1 flex-col transition-[margin] duration-200" style={helpDocked ? { marginRight: HELP_PANEL_WIDTH } : undefined}>
        <Topbar live={live} />
        <main id="main" ref={mainRef} className="min-w-0 flex-1" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
      <CommandPalette />
      <NotificationsDrawer />
      <HelpPanel />
      <TourRunner />
      <ShortcutsDialog />
      <GlobalCreateTask />
      <PwaBootstrap />
    </div>
  )
}

function MobileNav() {
  const open = useUI((s) => s.mobileNavOpen)
  const t = useT()
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(v) => ui.set({ mobileNavOpen: v })}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/30 lg:hidden" />
        <DialogPrimitive.Content className="fixed inset-y-0 left-0 z-50 flex w-[260px] flex-col border-r border-border bg-surface shadow-pop lg:hidden" aria-describedby={undefined}>
          <DialogPrimitive.Title className="sr-only">{t('nav.main')}</DialogPrimitive.Title>
          <div className="flex h-14 items-center border-b border-border px-4">
            <Logo />
          </div>
          <SidebarNav collapsed={false} onNavigate={() => ui.set({ mobileNavOpen: false })} />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

function GlobalCreateTask() {
  const open = useUI((s) => s.createTaskOpen)
  const defaults = useUI((s) => s.createTaskDefaults)
  if (!open) return null
  return <TaskFormDialog open onOpenChange={(v) => ui.set({ createTaskOpen: v, createTaskDefaults: v ? defaults : undefined })} defaults={defaults} />
}

function ShortcutsDialog() {
  const open = useUI((s) => s.shortcutsOpen)
  const t = useT()
  const rows: [React.ReactNode, string][] = [
    [
      <>
        <Kbd>{MOD}</Kbd> <Kbd>K</Kbd>
      </>,
      t('shortcuts.palette'),
    ],
    [<Kbd key="/">/</Kbd>, t('shortcuts.search')],
    [<Kbd key="c">C</Kbd>, t('shortcuts.newTask')],
    [<Kbd key="[">[</Kbd>, t('shortcuts.sidebar')],
    [<Kbd key="?">?</Kbd>, t('shortcuts.help')],
    [
      <>
        <Kbd>J</Kbd> <Kbd>K</Kbd>
      </>,
      t('shortcuts.jk'),
    ],
    [
      <>
        <Kbd>Y</Kbd> <Kbd>N</Kbd>
      </>,
      t('shortcuts.yn'),
    ],
  ]
  const goRows = [...NAV.flatMap((s) => s.items), ...EXTRA_ROUTES].filter((i) => i.key)
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(v) => ui.set({ shortcutsOpen: v })}>
      <DialogContent title={t('shortcuts.title')} size="lg">
        <div className="grid gap-6 sm:grid-cols-2">
          <dl className="flex flex-col gap-2 text-[13px]">
            {rows.map(([k, label], i) => (
              <div key={i} className="flex items-center justify-between gap-3">
                <dt className="text-ink">{label}</dt>
                <dd className="flex gap-1">{k}</dd>
              </div>
            ))}
          </dl>
          <dl className="flex flex-col gap-2 text-[13px]">
            {goRows.map((i) => (
              <div key={i.id} className="flex items-center justify-between gap-3">
                <dt className="text-ink">{t(i.label)}</dt>
                <dd className="flex gap-1">
                  <Kbd>G</Kbd> <Kbd>{i.key!.toUpperCase()}</Kbd>
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </DialogContent>
    </DialogPrimitive.Root>
  )
}
